import {
  IonIcon,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonToolbar,
  useIonAlert,
  useIonViewWillEnter,
  useIonViewWillLeave,
  useIonModal,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { captureException } from '@sentry/react';
import { pencil, qrCode } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { t } from 'ttag';

import Layout, { ButtonType } from '@components/Layout';
import LeadSuccess from '@components/LeadSuccess';
// skipcq: JS-W1029
import RoutePath from '@custom-types/RoutePath';
import useLoading from '@hooks/useLoading';
import { useScanner } from '@hooks/useScanner';
import useToast from '@hooks/useToast';
import { getByCheckinCode } from '@pages/People/service';
import { generateRoute } from '@services/navigation-service';
import { getCompanyInfo } from '@state/company';
import { getEventInfo } from '@state/event';
import { CapacitorBarcodeScannerCameraDirection } from '~capacitor-plugins/barcode-scanner';

import Details, { Mode } from './Details';
import { useCreateLead, useGetCustomFieldsConfig, useUpdateLead } from './queries';
import { Lead, Payload } from './service';

const SegmentButton = styled(IonSegmentButton)`
  padding: 5px;
`;

enum Type {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Scanner = 'scanner',
  Manual = 'manual',
}

const selector = createSelector(getEventInfo, getCompanyInfo, (event, company) => ({
  eventId: event.id,
  companyId: company.id,
  companyName: company.name,
}));

const Add = () => {
  const history = useHistory();
  const { eventId, companyId, companyName } = useSelector(selector);
  const { mutateAsync: createFn } = useCreateLead(eventId, companyId);
  const { mutateAsync: updateFn } = useUpdateLead(eventId, companyId);
  const { data: customFieldsConfig } = useGetCustomFieldsConfig(eventId, companyId);
  const [isQualifyLead, setIsQualifyLead] = useState(false);
  const [lead, setLead] = useState<Lead | null>(null);
  const { presentLoading, dismissLoading } = useLoading();
  const showToast = useToast();
  const [showAlert] = useIonAlert();
  const [type, setType] = useState(Type.Manual);

  const showLeadSuccessToast = useCallback(() => {
    showToast(t`Success! Lead saved.`);
  }, [showToast]);

  const showScanError = useCallback(
    () =>
      showAlert({
        header: t`Unable to scan QR code`,
        message: t`Try again or verify using other methods.`,
        buttons: [
          {
            text: t`Ok`,
            role: 'confirm',
          },
        ],
      }),
    [showAlert]
  );

  const onScanned = useCallback(
    async (checkinCode?: string) => {
      if (!checkinCode) {
        return;
      }
      try {
        presentLoading();
        const response = await getByCheckinCode(eventId, checkinCode!);
        if (response?.[0]) {
          const { firstName, lastName, email, companyName: company, title: position } = response[0];
          setLead(
            await createFn({
              firstName,
              lastName,
              email,
              company,
              position,
            })
          );
          setIsQualifyLead(true);
          showLeadSuccessToast();
        } else {
          showScanError();
          setType(Type.Manual);
        }
      } catch (err) {
        showToast(t`Something went wrong. Please try again.`);
        captureException(err);
      } finally {
        dismissLoading();
      }
    },
    [
      createFn,
      dismissLoading,
      eventId,
      presentLoading,
      showLeadSuccessToast,
      showScanError,
      showToast,
    ]
  );

  const onCancel = useCallback(() => setType(Type.Manual), []);
  const onError = useCallback(
    (/* error: Error */) => {
      showScanError();
      onCancel();
    },
    [onCancel, showScanError]
  );

  const { scan } = useScanner({
    cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
    onScanned,
    onCancel,
    onError,
  });

  const onLeadSuccess = useCallback(
    (showLeadSuccess = true) => {
      if (showLeadSuccess) {
        showLeadSuccessToast();
      }
      history.replace(generateRoute(RoutePath.LeadHome));
    },
    [history, showLeadSuccessToast]
  );

  const handleSave = useCallback(
    async (payload: Payload) => {
      presentLoading();
      try {
        await createFn(payload);
        onLeadSuccess();
      } finally {
        dismissLoading();
      }
    },
    [createFn, dismissLoading, onLeadSuccess, presentLoading]
  );

  const handleSaveLeadQualifier = useCallback(
    async (payload: Payload) => {
      presentLoading();
      try {
        if (lead) {
          await updateFn({ leadId: lead.id, payload });
          onLeadSuccess();
        }
      } finally {
        dismissLoading();
      }
    },
    [dismissLoading, lead, onLeadSuccess, presentLoading, updateFn]
  );

  const handleTabChange = useCallback(
    (value: Type) => {
      setType(value);
      if (value === Type.Scanner) {
        scan();
      }
    },
    [scan]
  );

  useIonViewWillEnter(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const tab = queryParams.get('tab') as Type;

    if (tab) {
      setType(tab);
    }
  });

  useIonViewWillLeave(() => {
    // This handler is only triggered in case of a qualify lead screen
    // when a user added a lead qualifier and navigated to some other screen
    // without saving the lead qualifier
    // There is one issue though, while doing so, if a user navigate to the bottom tabs
    // and come back again to the Add lead screen, the user see the cached screen
    // the below line of code will reset the required state
    // This doesn't work even when we have history.replace, I think this issue is occuring
    // because we are blocking tab navigation, and myabe this blocking behavior doesn't go well
    // with tabs
    setIsQualifyLead(false);
  });

  const [showSuccessModal, hideSuccessModal] = useIonModal(LeadSuccess, {
    firstName: lead?.firstName,
    lastName: lead?.lastName,
    company: lead?.company,
    position: lead?.position,
    datetime: lead?.updatedAt,
    email: lead?.email,
    onDismiss: () => {
      hideSuccessModal();
      onLeadSuccess();
    },
  });

  useEffect(() => {
    // We only show the success modal when scanning a lead with no custom field qualifiers enabled
    if (isQualifyLead && lead && !(lead.customFields?.length > 0)) {
      showSuccessModal();
    }
  }, [customFieldsConfig, isQualifyLead, lead, showSuccessModal]);

  if (isQualifyLead && lead && lead.customFields?.length > 0) {
    return (
      <Layout title={t`Qualify Lead`} buttonType={ButtonType.None}>
        <Details
          mode={Mode.QualifyLead}
          leadDetails={lead}
          customFieldsConfig={customFieldsConfig}
          onSave={handleSaveLeadQualifier}
          onOkNavigationAlert={showLeadSuccessToast}
        />
      </Layout>
    );
  }

  return (
    <Layout wrapContent={false} title={companyName}>
      <IonToolbar>
        <IonSegment
          data-testid="tabs"
          // skipcq: JS-0417
          onIonChange={(e: CustomEvent) => handleTabChange(e.detail.value as Type)}
          value={type}
        >
          <SegmentButton value={Type.Scanner} layout="icon-start">
            <IonIcon icon={qrCode} />
            <IonLabel>{t`Scan Leads`}</IonLabel>
          </SegmentButton>
          <SegmentButton value={Type.Manual} layout="icon-start">
            <IonIcon icon={pencil} />
            <IonLabel>{t`Add Manually`}</IonLabel>
          </SegmentButton>
        </IonSegment>
      </IonToolbar>

      {!isQualifyLead && type === Type.Scanner && (
        <div
          style={{
            display: 'flex',
            flex: 1,
          }}
        />
      )}
      {!isQualifyLead && type === Type.Manual && (
        <Details onSave={handleSave} customFieldsConfig={customFieldsConfig} />
      )}
    </Layout>
  );
};

export default Add;
